import { useContext, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/Dialog';
import { allowedLanguages } from '@concord-platform/platform-types/allowedLanguages';
import { ExternalLink } from '@/components/ui/ExternalLink';
import { ConfigContext, WidgetStateContext } from '@/context';
import { cn } from '@concord-platform/ui/cn';
import { ConsentEvent } from '@/types';

type Props = {
  item: ConsentEvent;
};

export function ConsentHistoryItem({ item }: Props) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);

  const { topRef } = useContext(WidgetStateContext);
  const {
    consentCategories,
    consentHistoryConsentEventsTitle,
    consentHistoryConsentEventsName,
    consentHistoryConsentEventsDescription,
    consentHistoryConsentEventsDate,
    consentHistoryConsentEventsDisclosure,
    consentHistoryConsentEventsLink,
    getConsentEventStatus,
    config,
  } = useContext(ConfigContext);

  const theme =
    config?.regionTemplate?.settings?.privacyCenter?.theme || 'classic';

  const consentState = getConsentEventStatus(item);

  /** Format date string to user's locale */
  const formatDate = ({
    dateString,
    options,
  }: {
    dateString: string;
    options: object;
  }) => {
    // @ts-ignore userLanguage is IE only and typescript doesn't appear to know about it
    const userLang = navigator.language || navigator.userLanguage;
    const langKey = userLang.substring(0, 2);
    if (Object.prototype.hasOwnProperty.call(allowedLanguages, langKey)) {
      return new Intl.DateTimeFormat(userLang, options).format(
        new Date(dateString),
      );
    }
    const date = new Date(dateString);
    return date.toLocaleDateString('en', options);
  };

  const getConsentText = (
    consentItem: ConsentEvent,
    categoryField: string,
    consentField: string,
  ) => {
    if (consentItem.consentType.subcategory === 'privacy_policy') {
      const consentText = consentItem.consentType[consentField];
      const replacementText =
        consentCategories[consentItem.consentType.subcategory]?.title;

      if (/Privacy Policy/i.test(consentText)) {
        return consentText.replace(
          /Privacy Policy/i,
          replacementText || 'Privacy Policy',
        );
      }
      return consentText;
    }

    const hasSubcategory = Object.prototype.hasOwnProperty.call(
      consentCategories,
      consentItem.consentType.subcategory,
    );

    if (hasSubcategory) {
      return consentCategories[consentItem.consentType.subcategory][
        categoryField
      ];
    }
    return consentItem.consentType[consentField];
  };

  const body = (
    <div data-testid="cd-consent-item-modal">
      <div>
        <span className="font-bold">
          {consentHistoryConsentEventsName}
          {': '}
        </span>
        {getConsentText(item, 'title', 'name')}
      </div>
      <div>
        <span className="font-bold">
          {consentHistoryConsentEventsDescription}
          {': '}
        </span>
        {getConsentText(item, 'text', 'description')}
      </div>
      {item.url && (
        <div>
          <span className="font-bold">
            {consentHistoryConsentEventsLink}
            {': '}
          </span>
          <ExternalLink href={item.url}>
            {consentHistoryConsentEventsDisclosure}
          </ExternalLink>
        </div>
      )}
      <div>
        <span className="font-bold">{consentHistoryConsentEventsDate}</span>
        {': '}
        {formatDate({
          dateString: item.consentDate,
          options: {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
          },
        })}
      </div>
    </div>
  );

  return (
    <Dialog
      onOpenChange={() => {
        setModalOpen(!isModalOpen);
      }}
    >
      <DialogTrigger asChild>
        <button
          className="text-muted-foreground hover:text-primary hover:border-primary border-card-border rounded-button grid min-h-16 w-full items-center justify-between gap-4 whitespace-break-spaces border bg-transparent py-4 pl-6 hover:shadow-md md:flex"
          data-testid={`cd-${item.id}`}
        >
          <div className="flex flex-grow flex-col justify-center gap-1">
            <div
              className="text-left font-semibold"
              data-testid="cd-consent-history-label"
            >
              {getConsentText(item, 'title', 'name')}
            </div>
            <div
              className="text-left text-xs font-medium"
              data-testid="cd-consent-history-date"
            >
              {formatDate({
                dateString: item.consentDate,
                options: {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                },
              })}
            </div>
          </div>
          <div
            className={cn(
              'justify-left align rounded-r-button -my-4 flex min-w-24 items-center self-stretch md:justify-center md:px-5',
              item.consentState.toLowerCase() === 'declined'
                ? 'md:bg-accent-transparent bg-transparent'
                : 'md:bg-primary-transparent bg-transparent',
            )}
            data-testid="cd-consent-history-status-tag"
          >
            <span
              className={cn(
                'text-center text-xs font-semibold',
                item.consentState.toLowerCase() === 'declined'
                  ? 'text-accent-foreground'
                  : 'text-primary',
              )}
              data-testid="cd-consent-history-status-label"
            >
              {consentState}
            </span>
          </div>
        </button>
      </DialogTrigger>
      <DialogContent
        ref={modalRef}
        aria-describedby={undefined}
        className={`${theme} bg-background text-muted-foreground border-pc-border sm:max-w-[425px]`}
        container={topRef}
        id="consent-item-modal"
        onBlur={(e: React.FocusEvent) => {
          // trap focus on modal for screen readers
          if (modalRef.current && !modalRef.current.contains(e.relatedTarget)) {
            Array.from(modalRef.current.querySelectorAll('button'))[0].focus();
          }
        }}
      >
        <DialogHeader className="text-header">
          <DialogTitle>{consentHistoryConsentEventsTitle}</DialogTitle>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
